import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { toast } from 'react-toastify';
import Input from '../../components/common/InputField';
import FooterTrustChat from '../../components/common/FooterTrustChat';
import ButtonMain from '../../components/common/ButtonMain';
import PhoneNumberInput from '../../components/common/PhoneInput';
import OptionsSelectInput from '../../components/common/OptionsSelectInput';
import { resetUser } from '../../store/features/loginUser';
import { logoutAll } from '../../store/features/LogoutAll';
import {
  LANGUAGE_OPTIONS,
  TIMEZONE_OPTIONS,
} from '../../components/dashboardcomponents/SettingsV2/Admin/Workspace/constants';
import { ReactComponent as Dropdown } from '../../assets/images/dropdown-arrow.svg';
import './style.css';

const specialCharacterRegex = /(^\s|\s$|[^a-zA-Z0-9 ]|(\s.*\s))/;
const onlyNumericRegex = /^\d+$/;

function MainBoardingScreen(props) {
  const currentLanguage = useSelector(
    (state) => state.language.currentLanguage,
  );
  const t = useSelector(
    (state) => state.language.translations[currentLanguage],
  );

  const [stateName, setStateMethod] = useState({
    BusinessName: '',
    BusinessNameErrorMsg: '',
    BusinessNameInvalid: false,
    FormSubmitting: false,
    nameState: false,
  });
  const [phoneState, setPhoneState] = useState({
    phoneNumber: '',
    phoneNumberErrorMsg: '',
    phoneNumberInvalid: false,
    phoneStateValid: false,
  });

  const {
    setSelectOnBoarding,
    setPhoneBoarding,
    setBoardingName,
    setBoardingTimezone,
    setBoardingLanguage,
    boardingPhone,
    boardingName,
    boardingTimezone,
    boardingLanguage,
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [timezone, setTimezone] = useState(boardingTimezone || 'UTC');
  const [language, setLanguage] = useState(boardingLanguage || 'en');

  useEffect(() => {
    if (boardingName?.length > 2) {
      setStateMethod((prev) => ({
        ...prev,
        BusinessName: boardingName,
        nameState: true,
      }));
    }
    if (boardingPhone?.length > 0) {
      const e164 = boardingPhone.startsWith('+')
        ? boardingPhone
        : `+${boardingPhone}`;
      setPhoneState((prevState) => ({
        ...prevState,
        phoneNumber: boardingPhone.replace(/\D/g, ''),
        phoneStateValid: isValidPhoneNumber(e164),
      }));
    }
    if (boardingTimezone) {
      setTimezone(boardingTimezone);
    }
    if (boardingLanguage) {
      setLanguage(boardingLanguage);
    }
  }, [boardingName, boardingPhone, boardingTimezone, boardingLanguage]);

  const logoutHandler = async () => {
    const result = await dispatch(logoutAll());
    if (logoutAll.rejected.match(result)) {
      toast.error("Couldn't sign you out. Check your connection and try again.");
      return;
    }
    dispatch(resetUser());
    localStorage.removeItem('auth_token');
    navigate('/', { replace: true });
  };

  const EnableHandler = () => {
    setBoardingName(stateName.BusinessName);
    setPhoneBoarding(phoneState.phoneNumber);
    setBoardingTimezone(timezone);
    setBoardingLanguage(language);

    if (stateName.nameState && phoneState.phoneStateValid) {
      setSelectOnBoarding(1);
    }
  };

  const handlePhoneChange = (value) => {
    const phoneNumber = (value || '').replace(/\D/g, '');
    const e164 = phoneNumber ? `+${phoneNumber}` : '';

    if (phoneNumber && isValidPhoneNumber(e164)) {
      setPhoneState({
        phoneNumber,
        phoneNumberErrorMsg: '',
        phoneNumberInvalid: false,
        phoneStateValid: true,
      });
    } else {
      setPhoneState({
        phoneNumber,
        phoneNumberErrorMsg: t.PHONEERROR,
        phoneNumberInvalid: true,
        phoneStateValid: false,
      });
    }
  };

  const handleChange = (event) => {
    switch (event.target.name) {
    case 'BusinessName': {
      const businessName = event.target.value;
      let nextState;

      if (businessName?.length < 3) {
        nextState = {
          ...stateName,
          BusinessName: businessName,
          BusinessNameErrorMsg: t.BUSINESSERROR,
          BusinessNameInvalid: true,
          nameState: false,
        };
      } else if (specialCharacterRegex.test(businessName)) {
        nextState = {
          ...stateName,
          BusinessName: businessName,
          BusinessNameErrorMsg: t.SPECIALBUSINESSERROR,
          BusinessNameInvalid: true,
          nameState: false,
        };
      } else if (onlyNumericRegex.test(businessName)) {
        nextState = {
          ...stateName,
          BusinessName: businessName,
          BusinessNameErrorMsg:
            t.BUSINESSALPHABETERROR
            || 'Please enter at least one alphabet character',
          BusinessNameInvalid: true,
          nameState: false,
        };
      } else {
        nextState = {
          ...stateName,
          BusinessName: businessName,
          BusinessNameErrorMsg: '',
          BusinessNameInvalid: false,
          nameState: true,
        };
      }

      setStateMethod(nextState);
      break;
    }
    default:
      setStateMethod({
        ...stateName,
        [event.target.name]: event.target.value,
      });
    }
  };

  const canContinue = stateName.nameState && phoneState.phoneStateValid;

  return (
    <>
      <div className="OnBoarding-Container">
        <div className="inputDivStyle-onboarding ">
          <div className="textDiv__login">
            <p className="text__create">{t.ENTER_BS}</p>
          </div>
          <Input
            name="BusinessName"
            id="BusinessName"
            type="text"
            title={t.ENTER_NA}
            placeholder={t.ENTER_PLACE}
            value={stateName.BusinessName}
            onChange={(e) => handleChange(e)}
            error={stateName.BusinessNameErrorMsg}
            className={clsx(
              stateName.BusinessNameInvalid ? 'input__error' : '',
              stateName.nameState ? 'input__success' : '',
            )}
          />
          <br />
          <PhoneNumberInput
            title={t.ENTER_PHONE}
            placeholder={t.ENTER_CPH}
            value={phoneState.phoneNumber}
            onChange={handlePhoneChange}
            error={phoneState.phoneNumberErrorMsg}
            className={clsx(
              phoneState.phoneNumberInvalid ? 'input__error' : '',
              phoneState.phoneStateValid ? 'input__success' : '',
            )}
          />
          <br />
          <label htmlFor="default-timezone" className="customer_label">
            {t.ENTER_TIMEZONE || 'Default Timezone'}
          </label>
          <OptionsSelectInput
            id="default-timezone"
            name="default_timezone"
            value={timezone}
            onChange={setTimezone}
            options={TIMEZONE_OPTIONS}
            placeholder={t.ENTER_TIMEZONE_PLACE || 'Select timezone'}
            popupIcon={<Dropdown />}
          />
          <br />
          <label htmlFor="default-language" className="customer_label">
            {t.ENTER_LANGUAGE || 'Default Language'}
          </label>
          <OptionsSelectInput
            id="default-language"
            name="default_language"
            value={language}
            onChange={setLanguage}
            options={LANGUAGE_OPTIONS}
            placeholder={t.ENTER_LANGUAGE_PLACE || 'Select language'}
            popupIcon={<Dropdown />}
          />
          <div className="btn-class">
            <ButtonMain
              className={
                canContinue ? 'buttonStyleEnable' : 'buttonStyleDisabled'
              }
              title={t.BTN_CONTINUE}
              onClick={() => EnableHandler()}
              disabled={!canContinue}
            />
          </div>
          <p className="a__singIn">
            {t.SKIP_OB}
            <span className="b__signIn" onClick={logoutHandler}>
              {t.LOGOUT}
            </span>
          </p>
        </div>
      </div>
      <FooterTrustChat />
    </>
  );
}
MainBoardingScreen.propTypes = {
  setSelectOnBoarding: PropTypes.func.isRequired,
  setPhoneBoarding: PropTypes.func.isRequired,
  setBoardingName: PropTypes.func.isRequired,
  setBoardingTimezone: PropTypes.func.isRequired,
  setBoardingLanguage: PropTypes.func.isRequired,
  boardingPhone: PropTypes.string.isRequired,
  boardingName: PropTypes.string.isRequired,
  boardingTimezone: PropTypes.string.isRequired,
  boardingLanguage: PropTypes.string.isRequired,
};
export default MainBoardingScreen;
